import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { ThemeProvider } from 'styled-components';
import { ModalProps } from './types';
import { Icon, ModalContainer, ModalView } from './styles';
import { BasicIcon } from '..';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import { KEYS } from '../utils/constants';
import { disableScrollBody, enableScrollBody } from '../utils/scroll';

const Modal: React.FC<ModalProps> = (props) => {
  const {
    children,
    width,
    height,
    isOpen,
    onClose,
    testId,
    closeOnBlur = true,
    overAll = false,
    closeButton = true,
    isMobile,
  } = props;
  const wasHiddenRef = useRef<boolean>(false);
  const { isMobile: mobileTheme, actualTheme } = useContext(ThemeContext);
  const useMobileVersion = useMemo(() => (isMobile !== undefined ? isMobile : mobileTheme), [
    isMobile,
    mobileTheme,
  ]);
  function stopPropagation(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.stopPropagation();
  }

  useEffect(() => {
    if (isOpen) {
      const body = document.querySelector('body');
      wasHiddenRef.current = body.style.overflow === 'hidden';
      disableScrollBody();
    } else if (!wasHiddenRef.current) enableScrollBody();
    return () => {
      if (isOpen && !wasHiddenRef.current) enableScrollBody();
    };
  }, [isOpen]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === KEYS.Esc && closeButton) {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    },
    [closeButton, onClose],
  );

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', onKeyDown);
    } else window.removeEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onKeyDown]);

  return (
    <ThemeProvider theme={actualTheme}>
      <ModalContainer
        id="modal"
        overAll={overAll}
        isOpen={isOpen}
        onClick={closeOnBlur ? onClose : null}
      >
        <ModalView
          height={height}
          width={width}
          id={testId}
          onClick={stopPropagation}
          isOpen={isOpen}
        >
          {closeButton && (
            <Icon>
              <BasicIcon
                onClick={onClose}
                id={`${testId}-close-button`}
                name="cross"
                size={useMobileVersion ? 'small' : 'medium'}
              />
            </Icon>
          )}
          {children}
        </ModalView>
      </ModalContainer>
    </ThemeProvider>
  );
};

export default Modal;
